import { FC, PropsWithChildren, useContext, useEffect, useRef } from "react";

import { ConsentModeContext } from "../contexts/ConsentModeContext";

interface ClarityProviderProps {
  project?: { key: string; name: string } | null;
  waitForInitialLoad?: boolean;
}

const CLARITY_SCRIPT_ID = "clarity-script";

declare global {
  interface Window {
    __webpack_nonce__?: string;
  }
}

/**
 * ClarityProvider - Dynamically loads Microsoft Clarity script based on consent mode
 *
 * This provider:
 * - Loads Clarity script only when user has given functionality consent
 * - Uses the provided projectId to initialize the correct Clarity project
 * - Cleans up the script when unmounting or when projectId changes
 *
 * Usage:
 * <ClarityProvider projectId={CLARITY_PROJECT_IDS.DONOR}>
 *   {children}
 * </ClarityProvider>
 */
export const ClarityProvider: FC<PropsWithChildren<ClarityProviderProps>> = ({
  project,
  waitForInitialLoad,
  children,
}) => {
  const { hasAnalyticsConsentMode } = useContext(ConsentModeContext);
  const loadedProjectIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Only load Clarity if:
    // 1. We have already waited for the initial data to pick the right projectId
    // 2. User has given consent
    // 3. We have a valid projectId
    // 4. We haven't already loaded this projectId
    if (waitForInitialLoad || !hasAnalyticsConsentMode || !project?.key || loadedProjectIdRef.current === project.key) {
      // eslint-disable-next-line no-console
      console.warn("Clarity script not loaded");
      return;
    }

    // eslint-disable-next-line no-console
    console.log("Clarity script loaded", project.name);

    // Load Clarity script
    const script = document.createElement("script");
    script.id = CLARITY_SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;

    // Add nonce for CSP compliance
    if (window.__webpack_nonce__) {
      script.nonce = window.__webpack_nonce__;
    }

    script.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        ${window.__webpack_nonce__ ? `t.nonce="${window.__webpack_nonce__}";` : ""}
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${project.key}");
    `;

    document.head.appendChild(script);
    loadedProjectIdRef.current = project.key;

    // Cleanup function
    return () => {
      const scriptToRemove = document.getElementById(CLARITY_SCRIPT_ID);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      if (window.clarity) {
        delete window.clarity;
      }
      loadedProjectIdRef.current = null;
    };
  }, [waitForInitialLoad, project, hasAnalyticsConsentMode]);

  return <>{children}</>;
};
